import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { LuMail, LuShieldAlert } from 'react-icons/lu';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import zxcvbn from 'zxcvbn';
import { ResetUserPasswordToken } from './compiled_proto/com/celertech/baseserver/api/user/DownstreamAuthenticationUserProto';
import { Button } from './components/common/Button';
import PasswordStrengthBar from './components/common/PasswordStrengthBar';
import StatusIndicator from './components/common/StatusIndicator';
import {
    ResetPasswordWithTokenFormInput,
    ResetPasswordWithTokenFormValues,
    resetPasswordWithTokenSchema
} from './components/form/schema/resetPasswordWithTokenSchema';
import RHFInput from './components/inputs/RHFInput';
import { isWolfBTCPlatform } from './helpers/environmentHelper';
import { LoginFooter, LoginLogo } from './Login';
import { logToServer } from './services/LogService';
import { resetUserPasswordWithToken, sendResetPasswordEmail } from './services/UserService';
import useLanguage from './utils/hooks/useLanguage';
import { getPasswordValidations } from './utils/validators';

interface ExtendedResetPasswordWithTokenFormValues extends ResetPasswordWithTokenFormInput {
    server: unknown;
}

const defaultValues: Partial<ResetPasswordWithTokenFormInput> = {
    token: undefined,
    username: undefined,
    new_password: undefined,
    confirm_new_password: undefined
};

function ResetPassword() {
    const lang = useLanguage();

    const [searchParams] = useSearchParams();

    const navigate = useNavigate();
    const [resetPasswordResponse, setResetPasswordResponse] = useState<string>();
    const form = useForm<ExtendedResetPasswordWithTokenFormValues>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(resetPasswordWithTokenSchema)
    });
    const {
        watch,
        trigger,
        register,
        setError,
        setValue,
        handleSubmit,
        formState: { isSubmitting, isValid, errors }
    } = form;

    const onSubmit = async (data: ResetPasswordWithTokenFormInput) => {
        const dataValidated = data as ResetPasswordWithTokenFormValues;
        const { token, username, new_password, confirm_new_password } = dataValidated;
        if (new_password === confirm_new_password) {
            try {
                const resp = await resetUserPasswordWithToken(token, username, new_password);
                if (resp.succeeded) {
                    logToServer('info', `User [${username}] has changed password.`);
                    setResetPasswordResponse(resp.message[0]);
                    // handlers.close();
                } else {
                    const body = resp.message[0];
                    setError('server', { type: 'custom', message: body });
                    logToServer('info', `User [${username}] has trouble changing password.`);
                }
            } catch (error: any) {
                const msg = 'Something went wrong during password reset. Please try again.';
                setError('server', { type: 'custom', message: msg });
                logToServer('info', `User [${username}] has trouble changing password. ${error?.message}`);
            }
        } else {
            setError('confirm_new_password', {
                type: 'custom',
                message: lang.resetPassword.passwordMismatchError
            });
        }
    };

    const [new_password, confirm_new_password] = watch(['new_password', 'confirm_new_password']);

    useEffect(() => {
        if (confirm_new_password) trigger('confirm_new_password');
    }, [new_password, confirm_new_password]);

    const passwordStrength = useMemo(() => {
        if (!new_password) return 0;
        return zxcvbn(new_password).score;
    }, [new_password]);

    const validation = getPasswordValidations([
        {
            name: 'password:min-length',
            description: 'Password minumum length',
            type: 'integer',
            value: '10'
        },
        {
            name: 'password:complexity',
            description: 'Password complexity score',
            type: 'integer',
            value: '2'
        }
    ]);

    const isExpiredOrInvalid = useMemo(() => {
        if (errors['server'])
            return [
                'Unable to reset password',
                'Something went wrong during password reset.',
                'Something went wrong during password reset. Please try again.'
            ].includes(errors['server'].message as string);
    }, [errors['server']]);

    useEffect(() => {
        const token = searchParams.get('token');
        const username = searchParams.get('username');

        if (token && username) {
            setValue('token', token);
            setValue('username', username);
        } else {
            setError('server', { type: 'custom', message: lang.resetPassword.missingTokenError });
        }
    }, []);

    return (
        <div className="h-full bg-brand-background flex flex-col justify-center py-12 px-6">
            <LoginLogo />
            {/* <div className="absolute top-6 right-6 flex items-center gap-4">
                <ThemeButton className="w-4 h-4" />
                <LanguageButton className="w-4 h-4" />
            </div> */}
            {resetPasswordResponse && (
                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="p-6 sm:p-8 rounded-md sm:rounded-lg sm:px-10 bg-brand-background-dark border-black shadow-[0_20px_76px_33px_rgba(0,0,0,0.66)]">
                        <div className="text-lg text-brand-primary-light whitespace-nowrap text-center">
                            <p>{lang.resetPassword.successMessage}</p>
                        </div>
                        <div
                            className="mt-6 relative flex justify-center text-sm font-medium text-brand-neutral-300 hover:text-brand-text cursor-pointer"
                            onClick={() => navigate('/login')}>
                            {lang.resetPassword.backToLogin}
                        </div>
                    </div>
                </div>
            )}
            {!(resetPasswordResponse || isExpiredOrInvalid) && (
                <FormProvider {...form}>
                    <form className="relative" onSubmit={handleSubmit(onSubmit)}>
                        <div className="relative my-6 mx-auto w-full max-w-md">
                            <div className="p-4 sm:p-6 rounded-md sm:rounded-lg sm:px-8 bg-brand-background brand-shadow z-0">
                                <div className="absolute top-4 right-4">
                                    <StatusIndicator showLabel={false} size="md" />
                                </div>
                                <div className="flex flex-col gap-5">
                                    <div className="flex gap-1 items-center justify-center text-center text-sm mb-4">
                                        <p className="text-brand-text">{lang.resetPassword.title}</p>
                                    </div>
                                    <RHFInput
                                        {...register('username')}
                                        label={lang.resetPassword.username}
                                        placeholder={lang.resetPassword.username}
                                        type="text"
                                        required
                                        disabled
                                    />
                                    <input type="password" className="opacity-0 none absolute" />
                                    <div className={cn('', { 'space-y-3 sm:space-y-2': new_password })}>
                                        <RHFInput
                                            {...register('new_password')}
                                            label={lang.resetPassword.newPassword}
                                            placeholder={lang.resetPassword.newPassword}
                                            type="password"
                                            autoComplete="off"
                                            required
                                        />
                                        <PasswordStrengthBar
                                            password={new_password as string}
                                            currentComplexity={passwordStrength}
                                            passwordRequirement={validation}
                                        />
                                    </div>
                                    <RHFInput
                                        {...register('confirm_new_password')}
                                        label={lang.resetPassword.confirmNewPassword}
                                        placeholder={lang.resetPassword.confirmNewPassword}
                                        type="password"
                                        required
                                    />
                                    {errors['server'] && (
                                        <span className="text-brand-red italic">{errors['server'].message}</span>
                                    )}
                                    <Button
                                        className="rounded-md p-2 px-4"
                                        isLoading={isSubmitting}
                                        disabled={isSubmitting || !(passwordStrength >= 2) || !isValid}>
                                        {lang.resetPassword.changePasswordButton}
                                    </Button>
                                </div>
                                <div
                                    className="mt-6 relative flex justify-center text-sm font-medium text-brand-neutral-300 hover:text-brand-text cursor-pointer"
                                    onClick={() => navigate('/login')}>
                                    {lang.resetPassword.backToLogin}
                                </div>
                            </div>
                        </div>
                        {!isWolfBTCPlatform && <LoginFooter />}
                    </form>
                </FormProvider>
            )}
            {isExpiredOrInvalid && <RequestNewEmail />}
        </div>
    );
}

export default ResetPassword;

function RequestNewEmail() {
    const lang = useLanguage();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const [username, setUsername] = useState<string>('');

    const [resetPasswordResponse, setResetPasswordResponse] = useState<ResetUserPasswordToken>();
    const [error, setError] = useState<{ message: string }>({ message: '' });
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const onReset = async () => {
        setIsSubmitting(true);
        try {
            const user = await sendResetPasswordEmail(username);
            logToServer('info', `User [${username}] has requested password reset email.`);
            setResetPasswordResponse(user);
        } catch (error: any) {
            // handle error here
            const msg = 'Something went wrong during password reset request.';
            logToServer('info', `User [${username}] has trouble requesting password reset email. ${error?.message}`);
            setError({ message: msg });
        }
        setIsSubmitting(false);
    };
    useEffect(() => {
        const username = searchParams.get('username');
        setUsername(username || '');
    }, []);

    return (
        <div className="flex flex-col items-center justify-center p-4 relative">
            <div className="w-full max-w-md relative z-10">
                <div className="relative my-6 mx-auto w-full max-w-md">
                    <div className="p-4 sm:p-6 text-center rounded-md sm:rounded-lg sm:px-8 bg-brand-background brand-shadow z-0">
                        {resetPasswordResponse ? (
                            <Fragment>
                                <div className="w-16 h-16 bg-brand-neutral-700 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <LuMail className="text-brand-primary-light" size={32} strokeWidth={1.5} />
                                </div>
                                <h1 className="text-2xl font-semibold text-brand-text mb-2 tracking-tight">
                                    {lang.resetPassword.checkYourEmail}
                                </h1>
                                <p className="text-brand-neutral-400 text-sm mb-8 leading-relaxed px-2">
                                    {lang.resetPassword.weSentPasswordResetLink}
                                </p>
                                <div className="space-y-4">
                                    <p className="text-sm text-brand-neutral-400">
                                        {lang.resetPassword.didntReceiveEmail}{' '}
                                        <button
                                            onClick={() => navigate('/forgot-password')}
                                            className="text-brand-text font-medium hover:underline focus:outline-none">
                                            {lang.resetPassword.clickHereToResend}
                                        </button>
                                    </p>
                                </div>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <div className="w-16 h-16 bg-brand-neutral-700 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <LuShieldAlert className="text-brand-red" size={32} strokeWidth={1.5} />
                                </div>
                                <h1 className="text-2xl font-semibold text-brand-text mb-2 tracking-tight">
                                    {lang.resetPassword.tokenExpired}
                                </h1>
                                <p className="text-brand-neutral-400 text-sm mb-8 leading-relaxed px-2">
                                    {lang.resetPassword.thePasswordResetLinkHasExpired}
                                </p>
                                <Button
                                    onClick={onReset}
                                    className="bg-brand-neutral-700 hover:bg-brand-neutral-600 h-11"
                                    isLoading={isSubmitting}>
                                    {lang.resetPassword.requestNewRestLink}
                                </Button>
                                {error.message ? (
                                    <div className="text-brand-red text-xs break-all pt-3">
                                        <div>{error.message}</div>
                                        <div>{lang.resetPassword.pleaseReachOutToSupport}</div>
                                    </div>
                                ) : null}
                            </Fragment>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
